import { SCHEDULE_METADATA_KEY, ENGINE_SCHEDULES_KEY } from '../constants'
import { REPEAT_TYPE } from '../constants/repeatTypes'
import { fromApiSchedule } from '../adapters/fromApiSchedule'
import { fromLegacyV2Schedule, isV3ScheduleEntry } from '../adapters/fromLegacyV2Schedule'
import { toApiSchedule, toApiSchedules } from '../adapters/toApiSchedule'
import {
  getScheduleId,
  getScheduleVersion,
  normalizeSchedules,
} from 'routes/Studies/routes/Study/routes/Instruments/utils/multiSchedule'
import { createEmptySchedule } from './createEmptySchedule'
import { createEmptyAvailability } from './createEmptyAvailability'

const isContractSchedule = isV3ScheduleEntry

/** Full metadata.schedule[] persisted by the scheduling engine (not legacy single-object editor). */
export const isEngineScheduleMetadataArray = schedule =>
  Array.isArray(schedule) && schedule.length > 0 && schedule.every(isV3ScheduleEntry)

const readScheduleEntries = schedulePayload => {
  if (Array.isArray(schedulePayload)) return schedulePayload.filter(Boolean)
  if (schedulePayload && typeof schedulePayload === 'object') return [schedulePayload]
  return []
}

const mapStoredScheduleEntry = entry =>
  isContractSchedule(entry) ? fromApiSchedule(entry) : fromLegacyV2Schedule(entry)

const migrateLegacyRule = rule => {
  const schedule = { ...createEmptySchedule(), ...rule }
  if (schedule.repeat?.type && !schedule.repeat?.mode) {
    schedule.repeat = {
      ...schedule.repeat,
      mode: REPEAT_TYPE[schedule.repeat.type] || schedule.repeat.type,
    }
  }
  const availability = schedule.availability || {}
  if ((availability.from || availability.until || availability.fromTz) && !availability.dailyWindow) {
    schedule.availability = {
      ...createEmptyAvailability(),
      ...availability,
      dailyWindow: {
        enabled: !!(availability.from || availability.until),
        from: availability.from || '08:00',
        until: availability.until || '14:00',
        timezone: availability.fromTz || availability.untilTz || availability.timezone,
      },
    }
  }
  return schedule
}

export const normalizeScheduleDraft = payload => {
  const schedules = readScheduleEntries(payload).map(entry =>
    isContractSchedule(entry) ? fromApiSchedule(entry) : migrateLegacyRule(entry),
  )
  return { schedules: schedules.length ? schedules : [createEmptySchedule()] }
}

export const readSchedulesFromInstrument = instrument => {
  const metadata = instrument?.metadata || {}
  const schedulePayload = metadata[SCHEDULE_METADATA_KEY]
  const engineSchedules = metadata[ENGINE_SCHEDULES_KEY]

  const entries = readScheduleEntries(schedulePayload)
  if (entries.length) {
    return { schedules: entries.map(mapStoredScheduleEntry) }
  }
  if (engineSchedules?.rules?.length) {
    return normalizeEngineSchedules(engineSchedules)
  }
  return { schedules: [createEmptySchedule()] }
}

const schedulePayloadWithoutVersion = apiSchedule => {
  const next = { ...(apiSchedule || {}) }
  delete next.version
  return next
}

const isActiveEngineSchedule = (schedule, activeScheduleId, schedules) => {
  if (!activeScheduleId) return (schedules || []).length <= 1
  const scheduleId = getScheduleId(schedule)
  return schedule.id === activeScheduleId || scheduleId === activeScheduleId
}

/** Bump version only for the schedule tab being saved; others keep persisted version. */
export const applyEngineScheduleVersions = (schedules, instrument, { activeScheduleId } = {}) => {
  const persistedById = new Map(
    normalizeSchedules(instrument?.metadata?.schedule).map(entry => [getScheduleId(entry), entry]),
  )

  return (schedules || []).map(schedule => {
    const scheduleId = getScheduleId(schedule)
    const persisted = scheduleId ? persistedById.get(scheduleId) : undefined
    const previousVersion = getScheduleVersion(persisted)
    const active = isActiveEngineSchedule(schedule, activeScheduleId, schedules)

    if (!active) {
      if (previousVersion != null) return { ...schedule, version: previousVersion }
      return { ...schedule, version: getScheduleVersion(schedule) ?? 1 }
    }

    const nextApi = toApiSchedule(schedule)
    if (!persisted || previousVersion == null) {
      return { ...schedule, version: 1 }
    }

    const changed =
      JSON.stringify(schedulePayloadWithoutVersion(nextApi)) !==
      JSON.stringify(schedulePayloadWithoutVersion(persisted))

    return { ...schedule, version: changed ? previousVersion + 1 : previousVersion }
  })
}

export const toScheduleMetadataPayload = schedules => toApiSchedules(schedules)

/** Stable fingerprint of persisted engine schedules; bumps when Redux instrument metadata changes. */
export const getEngineScheduleRevision = instrument => {
  const entries = normalizeSchedules(instrument?.metadata?.schedule)
  if (!entries.length) {
    return `empty:${instrument?.id ?? 'new'}`
  }
  return entries
    .map(entry => `${getScheduleId(entry) || entry?.id || ''}:${getScheduleVersion(entry) ?? 0}`)
    .join('|')
}

/** @deprecated */
export const normalizeEngineSchedules = payload => {
  if (payload?.rules) {
    return { schedules: payload.rules.map(rule => migrateLegacyRule(rule)) }
  }
  return normalizeScheduleDraft(payload)
}

/** @deprecated */
export const toEngineSchedulesPayload = schedules => ({
  version: 1,
  rules: schedules,
})
